// src/presentation/pages/ClinicalExamination/sections/HearingSection.tsx
import React from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import EarIcon from "@/assets/hearing.svg?react";
import { useTranslation } from "react-i18next";

interface HearingSectionProps {
  hearingStatus: string;
  hearingAssistive: {
    usesHearingAid: boolean;
    hasCochlearImplant: boolean;
    usesSignLanguage: boolean;
  };
  onHearingStatusChange: (value: string) => void;
  onHearingAssistiveChange: (
    field: keyof HearingSectionProps["hearingAssistive"],
    checked: boolean,
  ) => void;
}

export const HearingSection: React.FC<HearingSectionProps> = ({
  hearingStatus,
  hearingAssistive,
  onHearingStatusChange,
  onHearingAssistiveChange,
}) => {
  const { t } = useTranslation("patients");
  return (
    <div className="space-y-4 pt-4">
      <div className="flex gap-2 items-center">
        <EarIcon />
        <h4 className="text-base font-semibold text-gray-800 border-b">
          {t("clinicalSections.hearing.title")}
        </h4>
      </div>

      {/* Hearing Status */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">
          {t("clinicalSections.hearing.statusLabel")}
        </p>
        <RadioGroup
          value={hearingStatus}
          onValueChange={onHearingStatusChange}
          className="space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="normal" id="hearing-normal" />
            <Label
              htmlFor="hearing-normal"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.normal")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="mild" id="hearing-mild" />
            <Label
              htmlFor="hearing-mild"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.mild")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="severe" id="hearing-severe" />
            <Label
              htmlFor="hearing-severe"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.severe")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="deaf" id="hearing-deaf" />
            <Label
              htmlFor="hearing-deaf"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.deaf")}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Assistive/Functional */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">
          {t("clinicalSections.hearing.assistiveFunctional")}
        </p>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hearing-aid"
              checked={hearingAssistive.usesHearingAid}
              onCheckedChange={(checked) =>
                onHearingAssistiveChange("usesHearingAid", checked as boolean)
              }
            />
            <Label
              htmlFor="hearing-aid"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.usesHearingAid")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="cochlear-implant"
              checked={hearingAssistive.hasCochlearImplant}
              onCheckedChange={(checked) =>
                onHearingAssistiveChange(
                  "hasCochlearImplant",
                  checked as boolean,
                )
              }
            />
            <Label
              htmlFor="cochlear-implant"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.hasCochlearImplant")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sign-language"
              checked={hearingAssistive.usesSignLanguage}
              onCheckedChange={(checked) =>
                onHearingAssistiveChange("usesSignLanguage", checked as boolean)
              }
            />
            <Label
              htmlFor="sign-language"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.hearing.usesSignLanguage")}
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};
